<script setup lang="ts">
// Page admin V2 « Réassignation clients » — transfère en masse le portefeuille
// (clients assignés via assignedCommercial) d'un commercial source vers un commercial cible.
// Accès : SUPER_ADMIN (guard router `requiresSuperAdmin` + endpoints back).
import { computed, onMounted, ref, watch } from 'vue'
import { useToast } from 'vue-toast-notification'
import { ArrowPathIcon, ArrowsRightLeftIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import { useClientReassignStore } from '@/v2/stores/useClientReassignStore'
import SearchableSelect, { type SelectOption } from '@/v2/components/common/SearchableSelect.vue'
import HelpButton from '@/components/common/help/HelpButton.vue'

const store = useClientReassignStore()
const $toast = useToast()

// ── Sélection source / cible ─────────────────────────────────────────────────
const sourceUuid = ref<string>('')
const targetUuid = ref<string>('')
const search = ref('')
const selected = ref<Set<string>>(new Set())

const confirmDialog = ref<HTMLDialogElement | null>(null)

const sourceLabel = computed(() => store.commercials.find((c) => c.uuid === sourceUuid.value)?.label ?? '')
const targetLabel = computed(() => store.commercials.find((c) => c.uuid === targetUuid.value)?.label ?? '')

const sourceOptions = computed<SelectOption[]>(() => store.commercials.map((c) => ({ value: c.uuid, label: c.label })))
const targetOptions = computed<SelectOption[]>(() =>
  store.commercials.map((c) => ({ value: c.uuid, label: c.label, disabled: c.uuid === sourceUuid.value })),
)

const selectedCount = computed(() => selected.value.size)
const allSelected = computed(() => store.clients.length > 0 && selected.value.size === store.clients.length)

const canReassign = computed(
  () => selectedCount.value > 0 && !!targetUuid.value && targetUuid.value !== sourceUuid.value && !store.isReassigning,
)

// ── Chargement des clients du commercial source ──────────────────────────────
async function loadClients() {
  selected.value = new Set()
  if (!sourceUuid.value) {
    store.reset()
    return
  }
  await store.fetchClientsByCommercial(sourceUuid.value, search.value)
}

watch(sourceUuid, loadClients)

// ── Sélection des lignes ─────────────────────────────────────────────────────
function toggleRow(uuid: string) {
  const next = new Set(selected.value)
  if (next.has(uuid)) next.delete(uuid)
  else next.add(uuid)
  selected.value = next
}

function toggleAll() {
  selected.value = allSelected.value ? new Set() : new Set(store.clients.map((c) => c.uuid))
}

// ── Réassignation ────────────────────────────────────────────────────────────
function openConfirm() {
  if (!canReassign.value) return
  confirmDialog.value?.showModal()
}

async function confirmReassign() {
  confirmDialog.value?.close()
  const uuids = [...selected.value]
  const newCommercialUuid = targetUuid.value
  const newCommercialLabel = targetLabel.value
  const result = await store.bulkReassign(uuids, newCommercialUuid)
  if (result) {
    $toast.success(`${result.updated} client(s) réassigné(s) vers ${newCommercialLabel}`)
    // On conserve la source et la cible sélectionnées (pour enchaîner d'autres réassignations
    // vers le même commercial) : on recharge la liste de la source — les clients transférés
    // en disparaissent — et on vide la sélection.
    selected.value = new Set()
    await loadClients()
  } else {
    $toast.error('Échec de la réassignation')
  }
}

onMounted(async () => {
  // Toujours repartir d'un état vide à l'arrivée sur la page (selects, recherche, sélection).
  sourceUuid.value = ''
  targetUuid.value = ''
  search.value = ''
  selected.value = new Set()
  store.reset()
  await store.fetchCommercials()
})
</script>

<template>
  <div class="mx-auto w-full max-w-7xl p-4 md:p-8">
    <header>
      <div class="flex items-center gap-1">
        <h1 class="text-2xl font-bold">Réassignation clients</h1>
        <HelpButton />
      </div>
      <p class="mt-1 max-w-prose text-base-content/80">
        Transférez en masse le portefeuille d'un commercial vers un autre. Sélectionnez un commercial source pour voir
        ses clients, cochez ceux à transférer, puis choisissez le commercial cible.
      </p>
    </header>

    <!-- ── Sélection source ──────────────────────────────────────────────── -->
    <section class="card mt-6 border border-base-300 bg-base-100 shadow-sm" aria-labelledby="source-title">
      <div class="card-body p-4 md:p-6">
        <h2 id="source-title" class="card-title text-lg font-semibold">Clients à réassigner</h2>

        <div class="flex flex-wrap items-end gap-4">
          <div class="min-w-64">
            <label class="label" for="source-select"><span class="label-text">Commercial source</span></label>
            <SearchableSelect
              id="source-select"
              v-model="sourceUuid"
              :options="sourceOptions"
              :disabled="store.isLoadingCommercials"
              clearable
            />
          </div>

          <div class="min-w-64 flex-1">
            <label class="label" for="source-search"><span class="label-text">Filtrer (nom / SIREN)</span></label>
            <div class="flex gap-2">
              <input
                id="source-search"
                v-model="search"
                type="text"
                class="input input-bordered w-full"
                placeholder="Nom ou SIREN…"
                :disabled="!sourceUuid"
                @keyup.enter="loadClients"
              />
              <button type="button" class="btn btn-ghost" :disabled="!sourceUuid || store.isLoadingClients" @click="loadClients">
                <ArrowPathIcon
                  class="h-5 w-5"
                  :class="{ 'animate-spin motion-reduce:animate-none': store.isLoadingClients }"
                  aria-hidden="true"
                />
                Rechercher
              </button>
            </div>
          </div>
        </div>

        <!-- États -->
        <div v-if="store.isLoadingClients && !store.clients.length" class="py-8 text-center">
          <span class="loading loading-spinner" aria-hidden="true"></span>
          <p class="mt-2 text-base-content/80">Chargement des clients…</p>
        </div>

        <p v-else-if="!sourceUuid" class="py-8 text-center text-base-content/80">
          Sélectionnez un commercial source pour afficher ses clients.
        </p>

        <p v-else-if="!store.clients.length" class="py-8 text-center text-base-content/80">
          Aucun client assigné à ce commercial.
        </p>

        <div v-else class="-mx-4 mt-2 overflow-x-auto md:mx-0">
          <div class="flex items-center justify-between px-4 md:px-0">
            <span class="text-sm text-base-content/80">
              {{ store.clients.length }} client(s) — {{ selectedCount }} sélectionné(s)
            </span>
          </div>
          <table class="table">
            <thead>
              <tr>
                <th scope="col" class="w-12">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-sm"
                    :checked="allSelected"
                    :indeterminate.prop="selectedCount > 0 && !allSelected"
                    aria-label="Tout sélectionner"
                    @change="toggleAll"
                  />
                </th>
                <th scope="col">Nom</th>
                <th scope="col">SIREN</th>
                <th scope="col">Code postal</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="client in store.clients"
                :key="client.uuid"
                class="cursor-pointer hover"
                @click="toggleRow(client.uuid)"
              >
                <td @click.stop>
                  <input
                    type="checkbox"
                    class="checkbox checkbox-sm"
                    :checked="selected.has(client.uuid)"
                    :aria-label="`Sélectionner ${client.name}`"
                    @change="toggleRow(client.uuid)"
                  />
                </td>
                <td class="font-medium">{{ client.name }}</td>
                <td class="tabular-nums">{{ client.siren ?? '—' }}</td>
                <td class="tabular-nums">{{ client.zip ?? '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- ── Cible + action ────────────────────────────────────────────────── -->
    <section class="card mt-6 border border-base-300 bg-base-100 shadow-sm" aria-labelledby="target-title">
      <div class="card-body p-4 md:p-6">
        <h2 id="target-title" class="card-title text-lg font-semibold">Commercial cible</h2>

        <div class="flex flex-wrap items-end gap-4">
          <div class="min-w-64">
            <label class="label" for="target-select"><span class="label-text">Réassigner vers</span></label>
            <SearchableSelect
              id="target-select"
              v-model="targetUuid"
              :options="targetOptions"
              :disabled="store.isLoadingCommercials"
              clearable
            />
          </div>

          <button
            type="button"
            class="btn btn-primary"
            :disabled="!canReassign"
            :aria-busy="store.isReassigning"
            @click="openConfirm"
          >
            <span v-if="store.isReassigning" class="loading loading-spinner loading-xs" aria-hidden="true"></span>
            <ArrowsRightLeftIcon v-else class="h-5 w-5" aria-hidden="true" />
            Réassigner ({{ selectedCount }})
          </button>
        </div>

        <p
          v-if="targetUuid && targetUuid === sourceUuid"
          class="mt-1 text-sm text-error"
        >
          Le commercial cible doit être différent du commercial source.
        </p>
      </div>
    </section>

    <!-- ── Modal de confirmation ─────────────────────────────────────────── -->
    <dialog ref="confirmDialog" class="modal">
      <div class="modal-box">
        <h3 class="text-lg font-semibold">Confirmer la réassignation</h3>
        <p class="mt-2 text-base-content/80">
          Réassigner <strong>{{ selectedCount }}</strong> client(s) de <strong>{{ sourceLabel }}</strong> vers
          <strong>{{ targetLabel }}</strong> ?
        </p>

        <div role="alert" class="alert alert-warning mt-4">
          <ExclamationTriangleIcon class="h-5 w-5 shrink-0" aria-hidden="true" />
          <span>
            Les rattachements vendeur synchronisés depuis Odoo seront également déplacés, mais pourront être ré-écrasés
            au prochain sync Odoo.
          </span>
        </div>

        <div class="modal-action">
          <form method="dialog">
            <button type="submit" class="btn btn-ghost">Annuler</button>
          </form>
          <button type="button" class="btn btn-primary" :disabled="store.isReassigning" @click="confirmReassign">
            <span v-if="store.isReassigning" class="loading loading-spinner loading-xs" aria-hidden="true"></span>
            Confirmer
          </button>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop">
        <button>Fermer</button>
      </form>
    </dialog>
  </div>
</template>
